import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

/**
 * Searchable project picker — drop-in for the old <select> of projects.
 *
 *   <ProjectSelect projects={projects} value={form.project_id}
 *     onChange={id => setForm(f => ({ ...f, project_id: id }))}
 *     placeholder="— No Project —" className="...same classes as the old select..." />
 *
 * onChange receives the project id as a string ('' for the empty option), like
 * e.target.value did. Type to filter by code, name or client (all words must match).
 * The list is position:fixed so it is never clipped inside scrolling modals.
 */
const labelOf = (p, format) => format === 'name-code'
  ? `${p.name || ''}${p.project_id ? ` (${p.project_id})` : ''}`
  : [p.project_id, p.name].filter(Boolean).join(' — ');

export default function ProjectSelect({
  projects = [], value, onChange, placeholder = '— Select —', allowEmpty = true,
  disabled = false, className = '', format = 'code-name', emptyLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = projects.find(p => String(p.id) === String(value ?? ''));

  const options = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const list = projects.filter(p => {
      if (!words.length) return true;
      const hay = `${p.project_id || ''} ${p.name || ''} ${p.client_name || ''}`.toLowerCase();
      return words.every(w => hay.includes(w));
    });
    return allowEmpty && !words.length ? [{ id: '', _empty: true }, ...list] : list;
  }, [projects, query, allowEmpty]);

  const place = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom;
    const height = Math.min(300, Math.max(below, r.top) - 12);
    // Wider than a small trigger, but always inside the window (a picker at the
    // right edge opens towards the left instead of running off-screen)
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const width = Math.min(Math.max(r.width, 320), vw - 16);
    const left = Math.max(8, Math.min(r.left, vw - width - 8));
    setPos({ left, width, top: below >= 220 || below >= r.top ? r.bottom + 4 : undefined,
      bottom: below >= 220 || below >= r.top ? undefined : window.innerHeight - r.top + 4, maxHeight: height });
  };

  const openList = () => {
    if (disabled) return;
    setQuery('');
    setActive(Math.max(0, options.findIndex(o => String(o.id) === String(value ?? ''))));
    place();
    setOpen(true);
  };
  const close = () => { setOpen(false); setQuery(''); };
  const pick = (o) => { onChange?.(o._empty ? '' : String(o.id)); close(); triggerRef.current?.focus(); };

  useLayoutEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => { setActive(0); }, [query]);

  // Close on outside click; follow the trigger while the page / modal scrolls
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => {
      if (!panelRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) close();
    };
    const onMove = e => { if (!panelRef.current?.contains(e.target)) place(); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // Keep the highlighted row in view
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(options.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (options[active]) pick(options[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); triggerRef.current?.focus(); }
    else if (e.key === 'Tab') close();
  };

  const shown = selected ? labelOf(selected, format) : (emptyLabel || placeholder);

  return (
    <>
      <button type="button" ref={triggerRef} disabled={disabled} onClick={() => (open ? close() : openList())}
        onKeyDown={e => { if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openList(); } }}
        className={`${className} flex items-center justify-between gap-2 text-left bg-white disabled:opacity-60`}
        style={{ minWidth: '10rem', maxWidth: '100%' }}
        aria-haspopup="listbox" aria-expanded={open} title={selected ? labelOf(selected, format) : ''}>
        <span className={`truncate ${selected ? '' : 'text-gray-500'}`}>{shown}</span>
        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>

      {open && pos && (
        <div ref={panelRef} role="listbox"
          style={{ position: 'fixed', left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 10000 }}
          className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={onKey}
              placeholder="Search code, name or client…" className="flex-1 min-w-0 text-sm outline-none bg-transparent" />
            {query && <button type="button" onClick={() => { setQuery(''); inputRef.current?.focus(); }} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>}
          </div>
          <div ref={listRef} className="overflow-y-auto py-1" style={{ maxHeight: Math.max(120, (pos.maxHeight || 300) - 44) }}>
            {options.length === 0 && <div className="px-3 py-3 text-sm text-gray-400">No project matches “{query}”</div>}
            {options.map((o, i) => {
              const isSel = o._empty ? !selected : String(o.id) === String(value ?? '');
              return (
                <div key={o._empty ? '__empty' : o.id} data-i={i} role="option" aria-selected={isSel}
                  onMouseEnter={() => setActive(i)} onMouseDown={e => { e.preventDefault(); pick(o); }}
                  className={`px-3 py-2 text-sm cursor-pointer ${i === active ? 'bg-blue-50' : ''} ${isSel ? 'font-semibold text-blue-700' : 'text-gray-700'}`}>
                  {o._empty ? <span className="text-gray-500">{placeholder}</span> : (
                    <>
                      <div className="break-words leading-snug">{labelOf(o, format)}</div>
                      {o.client_name && <div className="text-[11px] text-gray-400 truncate">{o.client_name}</div>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
